import { useState, useEffect, useMemo } from "react";
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  LayoutDashboard, 
  FileEdit, 
  Settings, 
  LogOut,
  Users,
  Search,
  Mail,
  Phone,
  Building2,
  MapPin,
  Calendar,
  ExternalLink,
  Loader2,
  CircleDot,
  CheckCircle2,
  Trophy,
  XCircle,
  ChevronRight,
  Download,
  Filter
} from "lucide-react";
import Logo from "../components/Logo";
import AppSidebar from "@/components/AppSidebar";

// Status configuration
const statusConfig = {
  new: { color: "bg-blue-100 text-blue-700 border-blue-200", label: "New", icon: CircleDot },
  contacted: { color: "bg-yellow-100 text-yellow-700 border-yellow-200", label: "Contacted", icon: Mail },
  qualified: { color: "bg-purple-100 text-purple-700 border-purple-200", label: "Qualified", icon: CheckCircle2 },
  won: { color: "bg-green-100 text-green-700 border-green-200", label: "Won", icon: Trophy },
  lost: { color: "bg-gray-100 text-gray-500 border-gray-200", label: "Lost", icon: XCircle }
};

export default function CustomersPage() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data);
    } catch (error) {
      toast.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Helper functions to extract customer data
  const getCustomerName = (customer) => {
    return customer.form_data["Full Name"] || customer.form_data["Name"] || customer.form_data["full_name"] || customer.form_data["name"] || "Unknown";
  };

  const getCustomerEmail = (customer) => {
    return customer.form_data["Email"] || customer.form_data["email"] || "—";
  };

  const getCustomerPhone = (customer) => {
    return customer.form_data["Phone"] || customer.form_data["Phone Number"] || customer.form_data["phone"] || customer.form_data["phone_number"] || "—";
  };

  const getCustomerCompany = (customer) => {
    return customer.form_data["Company Name"] || customer.form_data["Company"] || customer.form_data["company"] || customer.form_data["company_name"] || "—";
  };

  const getCustomerRole = (customer) => {
    return customer.form_data["Role / Title"] || customer.form_data["Role"] || customer.form_data["Title"] || customer.form_data["role"] || "—";
  };

  // Filter customers
  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      const name = getCustomerName(customer).toLowerCase();
      const email = getCustomerEmail(customer).toLowerCase();
      const company = getCustomerCompany(customer).toLowerCase();
      const phone = getCustomerPhone(customer).toLowerCase();
      
      const matchesSearch = searchQuery === "" || 
        name.includes(searchQuery.toLowerCase()) ||
        email.includes(searchQuery.toLowerCase()) ||
        company.includes(searchQuery.toLowerCase()) ||
        phone.includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === "all" || (customer.status || "new") === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [customers, searchQuery, statusFilter]);

  // Export to CSV
  const exportToCSV = () => {
    const headers = ["Name", "Email", "Phone", "Company", "Role", "Status", "AI Score", "Date Added"];
    const rows = filteredCustomers.map(c => [
      getCustomerName(c),
      getCustomerEmail(c),
      getCustomerPhone(c),
      getCustomerCompany(c),
      getCustomerRole(c),
      statusConfig[c.status || "new"].label,
      c.ai_score,
      new Date(c.created_at).toLocaleDateString()
    ]);
    
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `customers_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success("Customers exported to CSV");
  };

  // Status counts
  const statusCounts = useMemo(() => {
    const counts = { all: customers.length, new: 0, contacted: 0, qualified: 0, won: 0, lost: 0 };
    customers.forEach(c => {
      const status = c.status || "new";
      if (counts[status] !== undefined) counts[status]++;
    });
    return counts;
  }, [customers]);

  return (
    <div className="min-h-screen bg-zinc-50 flex" >
      <AppSidebar user={user} onLogout={handleLogout} />

      {/* Main Content */}
      <main className="flex-1 ml-64 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2" >
                Customers
              </h1>
              <p className="text-muted-foreground">
                All contacts who have filled out your intake forms.
              </p>
            </div>
            <Button 
              variant="outline" 
              className="rounded-full"
              onClick={exportToCSV}
              disabled={filteredCustomers.length === 0}
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>

          {/* Status Pills */}
          <div className="flex items-center gap-2 mb-6 flex-wrap">
            <Button
              variant={statusFilter === "all" ? "default" : "outline"}
              size="sm"
              className="rounded-full"
              onClick={() => setStatusFilter("all")}
            >
              All ({statusCounts.all})
            </Button>
            {Object.entries(statusConfig).map(([key, config]) => (
              <Button
                key={key}
                variant={statusFilter === key ? "default" : "outline"}
                size="sm"
                className={`rounded-full ${statusFilter === key ? '' : config.color}`}
                onClick={() => setStatusFilter(key)}
              >
                <config.icon className="w-3 h-3 mr-1" />
                {config.label} ({statusCounts[key]})
              </Button>
            ))}
          </div>

          {/* Search */}
          <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)] mb-6">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, phone, or company..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 rounded-full"
                  data-testid="search-customers-input"
                />
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredCustomers.length === 0 ? (
            <Card className="rounded-2xl border-none shadow-[0_4px_20px_rgb(0,0,0,0.04)]">
              <CardContent className="p-12 text-center">
                <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {customers.length === 0 ? "No customers yet" : "No matching customers"}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {customers.length === 0 
                    ? "Customers will appear here when they fill out your intake forms."
                    : "Try adjusting your search or filters."
                  }
                </p>
                {customers.length === 0 && (
                  <Button asChild className="rounded-full">
                    <Link to="/form-builder">
                      Create an Intake Form
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Link>
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="font-semibold">Name</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">Phone</TableHead>
                      <TableHead className="font-semibold">Company</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold">Score</TableHead>
                      <TableHead className="font-semibold">Added</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCustomers.map((customer, index) => {
                      const status = statusConfig[customer.status || "new"];
                      const StatusIcon = status.icon;
                      
                      return (
                        <motion.tr
                          key={customer.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.02 }}
                          className="hover:bg-muted/30 cursor-pointer transition-colors"
                          onClick={() => setSelectedCustomer(customer)}
                          data-testid={`customer-row-${customer.id}`}
                        >
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                                {getCustomerName(customer).charAt(0).toUpperCase()}
                              </div>
                              <span className="font-medium">{getCustomerName(customer)}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-muted-foreground">{getCustomerEmail(customer)}</span>
                          </TableCell>
                          <TableCell>
                            <span className="text-muted-foreground">{getCustomerPhone(customer)}</span>
                          </TableCell>
                          <TableCell>
                            <span className="text-muted-foreground">{getCustomerCompany(customer)}</span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={status.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {status.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold ${
                              customer.ai_score >= 70 ? 'bg-green-500' : 
                              customer.ai_score >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}>
                              {customer.ai_score}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {new Date(customer.created_at).toLocaleDateString()}
                            </span>
                          </TableCell>
                          <TableCell>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </TableCell>
                        </motion.tr>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Results count */}
          {filteredCustomers.length > 0 && (
            <p className="text-sm text-muted-foreground mt-4 text-center">
              Showing {filteredCustomers.length} of {customers.length} customers
            </p>
          )}
        </div>
      </main>

      {/* Customer Detail Modal */}
      <Dialog open={!!selectedCustomer} onOpenChange={() => setSelectedCustomer(null)}>
        <DialogContent className="max-w-lg">
          {selectedCustomer && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xl font-bold">
                    {getCustomerName(selectedCustomer).charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <DialogTitle className="text-xl" >
                      {getCustomerName(selectedCustomer)}
                    </DialogTitle>
                    <DialogDescription>
                      {getCustomerRole(selectedCustomer) !== "—" && (
                        <span>{getCustomerRole(selectedCustomer)}</span>
                      )}
                      {getCustomerCompany(selectedCustomer) !== "—" && (
                        <span> at {getCustomerCompany(selectedCustomer)}</span>
                      )}
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                {/* Status */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Status:</span>
                  <Badge variant="outline" className={statusConfig[selectedCustomer.status || "new"].color}>
                    {React.createElement(statusConfig[selectedCustomer.status || "new"].icon, { className: "w-3 h-3 mr-1" })}
                    {statusConfig[selectedCustomer.status || "new"].label}
                  </Badge>
                  <span className="text-sm text-muted-foreground ml-auto">
                    Score: <span className="font-semibold">{selectedCustomer.ai_score}</span>
                  </span>
                </div>

                <Separator />

                {/* Contact Details */}
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Contact Details</h4>
                  
                  {getCustomerEmail(selectedCustomer) !== "—" && (
                    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Email</p>
                        <a href={`mailto:${getCustomerEmail(selectedCustomer)}`} className="text-sm text-primary hover:underline">
                          {getCustomerEmail(selectedCustomer)}
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {getCustomerPhone(selectedCustomer) !== "—" && (
                    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Phone</p>
                        <a href={`tel:${getCustomerPhone(selectedCustomer)}`} className="text-sm text-primary hover:underline">
                          {getCustomerPhone(selectedCustomer)}
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {getCustomerCompany(selectedCustomer) !== "—" && (
                    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">Company</p>
                        <p className="text-sm">{getCustomerCompany(selectedCustomer)}</p>
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Tags */}
                {(selectedCustomer.tags || []).length > 0 && (
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider mb-2">Tags</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedCustomer.tags.map((tag) => (
                        <Badge key={tag} variant="outline">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Summary */}
                <div>
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider mb-2">AI Summary</h4>
                  <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-lg">
                    {selectedCustomer.ai_summary}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    className="flex-1 rounded-full"
                    onClick={() => {
                      setSelectedCustomer(null);
                      navigate('/dashboard');
                    }}
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    View in CRM
                  </Button>
                  {getCustomerEmail(selectedCustomer) !== "—" && (
                    <Button 
                      className="flex-1 rounded-full"
                      onClick={() => window.open(`mailto:${getCustomerEmail(selectedCustomer)}`, '_blank')}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Send Email
                    </Button>
                  )}
                </div>

                <p className="text-xs text-muted-foreground text-center">
                  Added {new Date(selectedCustomer.created_at).toLocaleString()}
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
